import React, { useState, useEffect, useMemo } from 'react';
import { collection, doc, getDoc, getDocs, query, where, setDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { TrendCompare } from '../components/SharedComponents';
import { raStartYear } from '../utils';
import { styrelseUtkast } from '../qlik';
import { BOLAG, RADER, raManader, MANAD_LABELS, summering, beraknaLikviditet } from './budgetModel';

// ── STYRELSERAPPORT ───────────────────────────────────────────────
// Koncernvyn i styrelsens format: Kort resultatrapport per bolag +
// Totalt (helår), likviditetsbudget per månad (koncern) och
// likviditetskurvan. Exporteras via kortmenyn (PNG/Excel) eller
// hela sidan via ↻ PDF-knappen i navigationen.

const ACCENT = '#44365C';
const btn = (active) => ({
  background: active ? ACCENT : 'transparent', color: active ? '#fff' : 'var(--text-secondary)',
  border: '1px solid var(--border)', borderRadius: 20, padding: '4px 12px',
  fontSize: 11.5, cursor: 'pointer', fontFamily: 'system-ui,sans-serif',
});
function raVal() {
  const y = raStartYear();
  const mk = (s) => `${String(s % 100).padStart(2, '0')}${String((s + 1) % 100).padStart(2, '0')}`;
  return [mk(y + 1), mk(y), mk(y - 1)];
}
const raLabel2 = (ra) => `RÅ ${ra.slice(0, 2)}/${ra.slice(2)}`;
const krFmt = (v) => `${Math.round(v).toLocaleString('sv-SE')} kr`;
const tkr = (v) => `${Math.round(v / 1000).toLocaleString('sv-SE')} tkr`;
// "2627" → "2728"
const nastaRa = (r) => r.slice(2) + String(Number(r.slice(2)) + 1).padStart(2, '0');
const GRON = '#1D6A5F'; const ROD = '#A32D2D';

// ── VERSIONSVAL PER BOLAG ──────────────────────────────────────────
// Versionsnamnen numreras per bolag: Hotell kan stå på P2 när SC och
// Stigmyhrs bara har P1. Att välja "P2" i koncernvyn fick därför de två
// andra att falla tillbaka på B0 — och prognosen blandade utfall med
// budget utan att det syntes. "Senaste prognos" tar i stället varje
// bolags högsta P-version.
const PROGNOS = 'Senaste prognos';
const pNr = (v) => (/^P\d+$/.test(v || '') ? Number(v.slice(1)) : -1);
async function hamtaBudget(bolagId, raX, version) {
  if (version === PROGNOS) {
    const snap = await getDocs(query(collection(db, 'intel_budget'), where('bolag', '==', bolagId), where('ra', '==', raX)));
    const ps = snap.docs.map((d) => d.data()).filter((d) => pNr(d.version) >= 0)
      .sort((a, b) => pNr(a.version) - pNr(b.version));
    if (ps.length) return ps[ps.length - 1];
  } else {
    const s = await getDoc(doc(db, 'intel_budget', `${bolagId}_${raX}_${version}`));
    if (s.exists()) return { ...s.data(), version };
  }
  // Fall tillbaka på B0 om bolaget saknar vald version
  const s = await getDoc(doc(db, 'intel_budget', `${bolagId}_${raX}_B0`));
  return s.exists() ? { ...s.data(), version: 'B0' } : null;
}

// ── 24-MÅNADERSKURVAN ──────────────────────────────────────────────
// Styrelsen vill se längre än ett räkenskapsår. Två år efter varandra
// blir en sammanhängande kurva bara om år två börjar på år ett:s
// utgående — därav glapp-kontrollen: hittar den en glipa är kedjan bruten
// och kurvan ljuger, vilket måste synas i stället för att slätas över.
function LangKurva({ punkter, brytIdx, anstandIdx }) {
  const [hov, setHov] = useState(null);
  if (!punkter || punkter.length < 2) return null;
  const W = 1000, H = 300, padL = 12, padR = 12, padT = 30, padB = 34;
  const n = punkter.length;
  const vals = punkter.map((d) => d.utgaende);
  let mn = Math.min(0, ...vals), mx = Math.max(0, ...vals);
  if (mn === mx) mx = mn + 1;
  const plotW = W - padL - padR, plotH = H - padT - padB;
  const x = (i) => padL + (i * plotW) / (n - 1);
  const y = (v) => padT + (1 - (v - mn) / (mx - mn)) * plotH;
  const y0 = y(0);
  const linje = punkter.map((d, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(d.utgaende).toFixed(1)}`).join(' ');
  const yta = `${linje} L${x(n - 1).toFixed(1)},${y0.toFixed(1)} L${x(0).toFixed(1)},${y0.toFixed(1)} Z`;
  const lagsta = punkter.reduce((a, d, i) => (d.utgaende < punkter[a].utgaende ? i : a), 0);
  const sista = punkter[n - 1];
  const hd = hov != null ? punkter[hov] : null;
  const cid = `lk${Math.round(y0)}${n}`;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" preserveAspectRatio="xMidYMid meet"
      style={{ display: 'block' }} onMouseLeave={() => setHov(null)}>
      <defs>
        <clipPath id={`${cid}-upp`}><rect x="0" y="0" width={W} height={Math.max(0, y0)} /></clipPath>
        <clipPath id={`${cid}-ned`}><rect x="0" y={y0} width={W} height={Math.max(0, H - y0)} /></clipPath>
      </defs>
      {/* årsbrytet */}
      {brytIdx != null && (
        <g>
          <line x1={x(brytIdx)} y1={padT - 14} x2={x(brytIdx)} y2={H - padB} stroke="var(--border)" strokeWidth="1" />
          <text x={x(brytIdx) - 6} y={padT - 18} textAnchor="end" fontSize="10" fill="#999" fontFamily="system-ui,sans-serif">{punkter[0].raLabel}</text>
          <text x={x(brytIdx) + 6} y={padT - 18} fontSize="10" fill="#999" fontFamily="system-ui,sans-serif">{punkter[n - 1].raLabel}</text>
        </g>
      )}
      {/* nollinjen = kassan tom */}
      <line x1={padL} y1={y0} x2={W - padR} y2={y0} stroke="var(--border)" strokeWidth="1.5" strokeDasharray="4 4" />
      <text x={padL + 2} y={y0 - 4} fontSize="10" fill="#999" fontFamily="system-ui,sans-serif">0</text>
      <path d={yta} fill={GRON} opacity="0.10" clipPath={`url(#${cid}-upp)`} />
      <path d={yta} fill={ROD} opacity="0.12" clipPath={`url(#${cid}-ned)`} />
      <path d={linje} fill="none" stroke={GRON} strokeWidth="2.5" clipPath={`url(#${cid}-upp)`} />
      <path d={linje} fill="none" stroke={ROD} strokeWidth="2.5" clipPath={`url(#${cid}-ned)`} />
      {/* anståndet slutbetalt — hämtas ur budgetraden, inte antaget */}
      {anstandIdx != null && (
        <g>
          <line x1={x(anstandIdx)} y1={padT} x2={x(anstandIdx)} y2={H - padB} stroke={ACCENT} strokeWidth="1" strokeDasharray="3 3" opacity="0.6" />
          <circle cx={x(anstandIdx)} cy={y(punkter[anstandIdx].utgaende)} r="4" fill={ACCENT} />
          <text x={Math.min(W - 150, x(anstandIdx) + 6)} y={padT + 11} fontSize="10.5" fontWeight="600" fill={ACCENT} fontFamily="system-ui,sans-serif">
            anståndet slutbetalt
          </text>
        </g>
      )}
      {/* lägsta punkten */}
      <circle cx={x(lagsta)} cy={y(punkter[lagsta].utgaende)} r="4" fill={ROD} />
      <text x={Math.min(W - 120, Math.max(4, x(lagsta) - 30))} y={y(punkter[lagsta].utgaende) + 18} fontSize="10.5" fontWeight="700" fill={ROD} fontFamily="system-ui,sans-serif">
        {tkr(punkter[lagsta].utgaende)}
      </text>
      {/* slutpunkten */}
      <circle cx={x(n - 1)} cy={y(sista.utgaende)} r="4" fill={sista.utgaende >= 0 ? GRON : ROD} />
      <text x={W - padR} y={y(sista.utgaende) - 9} textAnchor="end" fontSize="10.5" fontWeight="700" fill={sista.utgaende >= 0 ? GRON : ROD} fontFamily="system-ui,sans-serif">
        {tkr(sista.utgaende)}
      </text>
      {/* månadsetiketter var tredje */}
      {punkter.map((d, i) => (i % 3 === 0 ? (
        <text key={i} x={x(i)} y={H - 10} textAnchor="middle" fontSize="9.5" fill="#999" fontFamily="system-ui,sans-serif">
          {d.label}{d.label === 'jul' ? ` ${d.ym.slice(2, 4)}` : ''}
        </text>
      ) : null))}
      {punkter.map((d, i) => {
        const xl = i === 0 ? 0 : (x(i - 1) + x(i)) / 2;
        const xr = i === n - 1 ? W : (x(i) + x(i + 1)) / 2;
        return <rect key={`h${i}`} x={xl} y="0" width={Math.max(1, xr - xl)} height={H} fill="transparent" onMouseEnter={() => setHov(i)} />;
      })}
      {hd && (
        <g>
          <line x1={x(hov)} y1={padT} x2={x(hov)} y2={H - padB} stroke="var(--border)" strokeWidth="1" />
          <circle cx={x(hov)} cy={y(hd.utgaende)} r="3.5" fill={hd.utgaende >= 0 ? GRON : ROD} />
          <text x={Math.min(W - 170, Math.max(4, x(hov) - 60))} y={padT + 11} fontSize="11" fontWeight="600" fill="var(--text)" fontFamily="system-ui,sans-serif">
            {hd.label} {hd.ym.slice(2, 4)}: {tkr(hd.utgaende)}
          </text>
        </g>
      )}
    </svg>
  );
}

export default function BudgetRapport() {
  const [ra, setRa] = useState(raVal()[1]);
  const [version, setVersion] = useState('B0');
  const [vy, setVy] = useState('resultat');   // resultat | likvid
  const [ohUt, setOhUt] = useState(false);    // O/H som egen kolumn
  const [versioner, setVersioner] = useState(['B0']);
  const [docs, setDocs] = useState(null); // bolagId → { rader, forutsattningar, version }
  // Kommentarer till styrelsen (AI-utkast, redigeras och sparas per RÅ+version)
  const [komm, setKomm] = useState({ sammanfattning: '', likviditetskommentar: '', perBolag: {} });
  const [kommDirty, setKommDirty] = useState(false);
  const [kommSparar, setKommSparar] = useState(false);
  const [kommJobb, setKommJobb] = useState(false);
  const [kommFel, setKommFel] = useState('');
  const [kommMsg, setKommMsg] = useState('');
  const [anteckning, setAnteckning] = useState('');

  const manader = useMemo(() => raManader(ra), [ra]);
  // Nästa räkenskapsår — underlaget till 24-månaderskurvan
  const [docs2, setDocs2] = useState(null);
  const ra2 = useMemo(() => nastaRa(ra), [ra]);
  const manader2 = useMemo(() => raManader(ra2), [ra2]);

  // Ladda sparade kommentarer för RÅ+version
  useEffect(() => {
    let live = true;
    (async () => {
      const s = await getDoc(doc(db, 'intel_budget', `rapport_${ra}_${version}`));
      if (!live) return;
      const d = s.exists() ? s.data() : {};
      setKomm({
        sammanfattning: d.sammanfattning || '',
        likviditetskommentar: d.likviditetskommentar || '',
        perBolag: d.perBolag || {},
      });
      setKommDirty(false); setKommMsg(''); setKommFel('');
    })().catch(console.error);
    return () => { live = false; };
  }, [ra, version]);

  useEffect(() => {
    let live = true;
    (async () => {
      const snap = await getDocs(query(collection(db, 'intel_budget'), where('ra', '==', ra)));
      const vs = [...new Set(snap.docs.map((d) => d.data().version))].sort();
      if (!live) return;
      // "Senaste prognos" läggs till så fort något bolag har en P-version
      const lista = vs.length ? vs : ['B0'];
      if (vs.some((v) => /^P\d+$/.test(v))) lista.splice(lista.includes('B0') ? 1 : 0, 0, PROGNOS);
      setVersioner(lista);
      // Öppna senaste prognosen när någon finns — den färskaste synen på året
      setVersion(lista.includes(PROGNOS) ? PROGNOS : (lista[lista.length - 1] || 'B0'));
    })().catch(console.error);
    return () => { live = false; };
  }, [ra]);

  useEffect(() => {
    let live = true;
    (async () => {
      const out = {};
      for (const b of BOLAG) {
        const d = await hamtaBudget(b.id, ra, version);
        if (d) out[b.id] = d;
      }
      if (live) setDocs(out);
    })().catch(console.error);
    return () => { live = false; };
  }, [ra, version]);

  useEffect(() => {
    let live = true;
    (async () => {
      const out = {};
      for (const b of BOLAG) {
        const d = await hamtaBudget(b.id, ra2, version);
        if (d) out[b.id] = d;
      }
      if (live) setDocs2(out);
    })().catch(console.error);
    return () => { live = false; };
  }, [ra2, version]);

  // Fastställd budget (B0) som jämförelsepunkt när en prognosversion är vald
  const [docsB0, setDocsB0] = useState(null);
  useEffect(() => {
    if (version === 'B0') { setDocsB0(null); return undefined; }
    let live = true;
    (async () => {
      const out = {};
      for (const b of BOLAG) {
        const s = await getDoc(doc(db, 'intel_budget', `${b.id}_${ra}_B0`));
        if (s.exists()) out[b.id] = s.data();
      }
      if (live) setDocsB0(out);
    })().catch(console.error);
    return () => { live = false; };
  }, [ra, version]);

  const D = useMemo(() => {
    if (!docs) return null;
    const perBolag = {};
    for (const b of BOLAG) {
      const d = docs[b.id];
      if (!d) continue;
      const S = summering(d.rader || {}, RADER[b.id], manader);
      const helar = (falt) => manader.reduce((s, m) => s + S[m][falt], 0);
      const radHelar = (dok, radId) => manader.reduce((s, m) => s + (((dok.rader || {})[radId] || {})[m] || 0), 0);
      perBolag[b.id] = {
        S,
        version: d.version,
        forutsattningar: d.forutsattningar || '',
        rr: {
          intakter: helar('intakter'), material: helar('direkta'), ovriga: helar('ovriga'),
          personal: helar('personal'), driftsresultat: helar('driftsresultat'),
          avskr: helar('avskr'), finans: helar('finans'), resultat: helar('resultat'),
          // Likvidposter som helår — avskrivningar byts mot amorteringar
          amortering: radHelar(d, 'likvid:amortering'),
          amortSkatt: radHelar(d, 'likvid:amortering_skatt'),
          investering: radHelar(d, 'likvid:investering'),
          // Inbetalningar bland likvidposterna — saknades helt i likvidvyn
          amorteringIn: radHelar(d, 'likvid:amortering_in'),
          extraNetto: radHelar(d, 'likvid:extra_in') - radHelar(d, 'likvid:extra_ut'),
          // O/H per kostnadsgrupp — rader märkta oh:true i modellen. Generiskt
          // så att fler O/H-rader kan tillkomma utan att rapporten ändras.
          ohOvrigt: RADER[b.id].filter((r) => r.oh && r.grupp === 'ovrigt').reduce((s, r) => s + radHelar(d, r.id), 0),
          ohPersonal: RADER[b.id].filter((r) => r.oh && r.grupp === 'personal').reduce((s, r) => s + radHelar(d, r.id), 0),
          ohIntakt: radHelar(d, 'intakt:oh'),
        },
      };
    }
    const bolagMed = BOLAG.filter((b) => perBolag[b.id]);

    // Kassaflödet per bolag räknas FÖRE summeringen, så kortrapportens
    // likvidresultat kan bygga på samma modell som månadstabellen. Tidigare
    // var det driftsresultat minus kassaposter rakt av, vilket varken kände
    // till betalningsförskjutningarna eller de ingående skulderna — därför
    // visade portalen två olika likvidresultat.
    const perBolagLikvid = {};
    for (const b of bolagMed) {
      perBolagLikvid[b.id] = beraknaLikviditet({
        bolag: b.id, rader: docs[b.id].rader || {}, likvid: docs[b.id].likvid || {}, manader,
      });
      const rr = perBolag[b.id].rr;
      // Kassaflödet så som det faktiskt landar i månadstabellen
      rr.kassaNetto = perBolagLikvid[b.id].perManad.reduce((s, p) => s + p.netto, 0);
      // Samma poster på periodiserad grund — skillnaden är förskjutningen
      // Underliggande förmåga att bygga kapital — vad året genererar om
      // man bortser från NÄR fakturorna råkar betalas.
      rr.likvidFore = rr.driftsresultat - rr.finans - rr.amortering - rr.amortSkatt
        - rr.investering + rr.amorteringIn + rr.extraNetto;
      rr.kassajustering = rr.kassaNetto - rr.likvidFore;
      rr.likvidResultat = rr.kassaNetto;
    }

    const tot = {};
    for (const f of ['intakter', 'material', 'ovriga', 'personal', 'driftsresultat', 'avskr', 'finans', 'resultat',
      'amortering', 'amortSkatt', 'investering', 'likvidResultat', 'ohOvrigt', 'ohPersonal', 'ohIntakt', 'amorteringIn', 'extraNetto', 'kassajustering', 'kassaNetto', 'likvidFore']) {
      tot[f] = bolagMed.reduce((s, b) => s + perBolag[b.id].rr[f], 0);
    }
    tot.ohKostnad = tot.ohOvrigt + tot.ohPersonal;
    // O/H som EGEN kolumn i stället för utspritt i bolagen. Hotell bär
    // koncernens ledning och administration, vilket får bolaget att se
    // olönsamt ut fast det är gruppens kostnad. Utbrutet blir de tre
    // bolagen jämförbara på ren drift och O/H-kolumnen visar nettot.
    const ohKol = { intakter: tot.ohIntakt, ovriga: tot.ohOvrigt, personal: tot.ohPersonal };
    ohKol.driftsresultat = ohKol.intakter - ohKol.ovriga - ohKol.personal;
    ohKol.material = 0; ohKol.avskr = 0; ohKol.finans = 0;
    ohKol.amortering = 0; ohKol.amortSkatt = 0; ohKol.investering = 0;
    ohKol.amorteringIn = 0; ohKol.extraNetto = 0; ohKol.kassajustering = 0;
    ohKol.likvidFore = ohKol.driftsresultat;
    ohKol.resultat = ohKol.driftsresultat;
    ohKol.likvidResultat = ohKol.driftsresultat;
    // Bolagens siffror rensade från O/H — varje post lyfts ur sin egen grupp
    const utanOh = {};
    for (const b of bolagMed) {
      const rr = perBolag[b.id].rr;
      const netto = rr.ohOvrigt + rr.ohPersonal - rr.ohIntakt; // vad O/H kostar bolaget
      utanOh[b.id] = {
        ...rr,
        intakter: rr.intakter - rr.ohIntakt,
        ovriga: rr.ovriga - rr.ohOvrigt,
        personal: rr.personal - rr.ohPersonal,
        driftsresultat: rr.driftsresultat + netto,
        resultat: rr.resultat + netto,
        likvidResultat: rr.likvidResultat + netto,
        likvidFore: rr.likvidFore + netto,
      };
    }
    // Likviditet per månad — koncern. Använder SAMMA modell som
    // likviditetsmatrisen (beraknaLikviditet): betalningsregler, förskott och
    // ingående saldon. Den gamla formeln här räknade på periodiserade
    // resultatrader och startade kurvan på noll, så portalen visade två
    // olika likviditetssiffror på två ställen.
    const ingaendeBank = bolagMed.reduce((s, b) => s + ((docs[b.id].likvid || {}).ingaendeBank || 0), 0);

    let saldo = ingaendeBank;
    const likvid = manader.map((m, i) => {
      const sum = (falt) => bolagMed.reduce((s, b) => s + perBolag[b.id].S[m][falt], 0);
      const rad = (falt) => bolagMed.reduce((s, b) => s + perBolagLikvid[b.id].perManad[i][falt], 0);
      const inbet = rad('inbet'), utbet = rad('utbet');
      const ingaende = saldo;
      // justering = avstämt banksaldo i något bolag; flyttar saldot utan att vara ett flöde
      saldo += inbet - utbet + rad('justering');
      return {
        label: MANAD_LABELS[i], inbet, utbet, ingaende, utgaende: saldo,
        likvid: inbet - utbet, resultat: sum('resultat'),
      };
    });
    let ackR = 0;
    const kurva = likvid.map((r) => { ackR += r.resultat; return { label: r.label, value: Math.round(r.utgaende), prev: Math.round(ackR) }; });
    const lagsta = likvid.reduce((a, r) => (r.utgaende < a.utgaende ? r : a), likvid[0]);
    return { perBolag, bolagMed, tot, likvid, kurva, ingaendeBank, lagsta, ohKol, utanOh };
  }, [docs, manader]);

  // ── 24 månader i en följd ────────────────────────────────────────
  // År två räknas med samma modell och läggs efter år ett. Kurvan är
  // sammanhängande bara om år två:s ingående bank = år ett:s utgående;
  // stämmer det inte är kedjan bruten och glappet rapporteras öppet.
  const LANG = useMemo(() => {
    if (!D || !docs2) return null;
    const bolag2 = BOLAG.filter((b) => docs2[b.id]);
    if (!bolag2.length) return { saknas: true };
    const per2 = {};
    for (const b of bolag2) {
      per2[b.id] = beraknaLikviditet({
        bolag: b.id, rader: docs2[b.id].rader || {}, likvid: docs2[b.id].likvid || {}, manader: manader2,
      });
    }
    const ing2 = bolag2.reduce((sum, b) => sum + ((docs2[b.id].likvid || {}).ingaendeBank || 0), 0);
    let saldo = ing2;
    const ar2 = manader2.map((m, i) => {
      saldo += bolag2.reduce((sum, b) => sum + per2[b.id].perManad[i].netto + per2[b.id].perManad[i].justering, 0);
      return { ym: m, label: MANAD_LABELS[i], utgaende: saldo, raLabel: raLabel2(ra2) };
    });
    const ar1 = D.likvid.map((r, i) => ({
      ym: manader[i], label: r.label, utgaende: r.utgaende, raLabel: raLabel2(ra),
    }));
    const punkter = [...ar1, ...ar2];
    const glapp = Math.round(ing2 - ar1[ar1.length - 1].utgaende);

    // Anståndet: sista månaden med en amortering på skatteanståndet.
    // Läses ur budgetraden i stället för att antas — ändras planen
    // flyttar sig markören med.
    let anstandIdx = null;
    const alla = [...manader, ...manader2];
    alla.forEach((m, i) => {
      const dok = i < manader.length ? docs : docs2;
      const v = BOLAG.reduce((sum, b) => sum + (((dok[b.id]?.rader || {})['likvid:amortering_skatt'] || {})[m] || 0), 0);
      if (v) anstandIdx = i;
    });
    const lagsta = punkter.reduce((a, d) => (d.utgaende < a.utgaende ? d : a), punkter[0]);
    return {
      punkter, glapp, anstandIdx, lagsta, brytIdx: manader.length,
      // Kurvans punkter är utgående per månad, så start måste tas ur
      // ingående bank — annars visar rutan julis utgående som "ingående".
      start: D.ingaendeBank, slut: punkter[punkter.length - 1].utgaende,
      versioner: bolag2.map((b) => `${b.namn.replace(' AB', '')} ${docs2[b.id].version}`).join(' · '),
    };
  }, [D, docs2, manader, manader2, ra, ra2]);

  // ── Prognos mot fastställd budget ────────────────────────────────
  // Med en P-version vald: hittills (utfallsmånaderna, utfall mot B0) och
  // helår (B0 mot prognosen = utfall + resterande budget). Det är svaret
  // på "följer vi budgeten" och "var landar året" på samma sida.
  // Fjolårets bokförda utfall (SIE-uppladdningen) per bolag → ym → summor.
  // Laddas en gång; bara det som behövs för "Fg år"-kolumnen.
  const [bokFg, setBokFg] = useState(null);
  useEffect(() => {
    let live = true;
    (async () => {
      const out = {};
      for (const b of BOLAG) {
        const snap = await getDocs(query(collection(db, 'intel_bokforing'), where('bolag', '==', b.id)));
        out[b.id] = {};
        snap.forEach((d) => { const v = d.data(); if (v.ym && v.summor) out[b.id][v.ym] = v.summor; });
      }
      if (live) setBokFg(out);
    })().catch((e) => { console.error('bokföring fg år:', e); if (live) setBokFg({}); });
    return () => { live = false; };
  }, []);

  const JMF = useMemo(() => {
    if (!D || !docsB0 || version === 'B0') return null;
    const falt = ['intakter', 'direkta', 'ovriga', 'personal', 'driftsresultat', 'avskr', 'finans', 'resultat'];
    const summa = (S, man, f) => man.reduce((s, m) => s + (S[m]?.[f] || 0), 0);
    // Bokföringens summor i samma fält som summering() — för fjolårskolumnen
    const fgFalt = (s) => {
      const drift = (s.intakt || 0) - (s.vk || 0) - (s.ovrigt || 0) - (s.personal || 0);
      return {
        intakter: s.intakt || 0, direkta: s.vk || 0, ovriga: s.ovrigt || 0, personal: s.personal || 0,
        driftsresultat: drift, avskr: s.avskr || 0, finans: s.finans || 0,
        resultat: drift - (s.avskr || 0) - (s.finans || 0),
      };
    };
    const ettArTidigare = (ym) => `${Number(ym.slice(0, 4)) - 1}${ym.slice(4)}`;
    const per = {};
    const tot = {};
    for (const f of falt) tot[f] = { hBud: 0, hUtf: 0, fg: 0, b0: 0, p: 0 };
    const tomPerBolag = [];
    const fgSaknas = [];
    for (const b of D.bolagMed) {
      if (!docsB0[b.id]) continue;
      const SB0 = summering(docsB0[b.id].rader || {}, RADER[b.id], manader);
      const SP = D.perBolag[b.id].S;
      const tom = docs[b.id]?.utfallTom || '';
      const utfMan = tom ? manader.filter((m) => m <= tom) : [];
      tomPerBolag.push({ namn: b.namn.replace(' AB', ''), tom, version: D.perBolag[b.id].version });
      // Samma månader ett år tidigare, ur bokföringen
      const fgBok = (bokFg || {})[b.id] || {};
      const fgMan = utfMan.map(ettArTidigare);
      const fgPer = fgMan.map((m) => (fgBok[m] ? fgFalt(fgBok[m]) : null));
      if (fgMan.some((m, i) => !fgPer[i])) fgSaknas.push(b.namn.replace(' AB', ''));
      per[b.id] = {};
      for (const f of falt) {
        const v = {
          hBud: summa(SB0, utfMan, f), hUtf: summa(SP, utfMan, f),
          fg: fgPer.reduce((s, x) => s + (x ? x[f] : 0), 0),
          b0: summa(SB0, manader, f), p: summa(SP, manader, f),
        };
        per[b.id][f] = v;
        for (const k of Object.keys(v)) tot[f][k] += v[k];
      }
    }
    const toms = [...new Set(tomPerBolag.map((t) => t.tom))];
    return { per, tot, tomPerBolag, gemensamTom: toms.length === 1 ? toms[0] : null, fgSaknas, fgLaddad: !!bokFg };
  }, [D, docsB0, docs, version, manader, bokFg]);

  // ── AI-utkast: budget + likviditet + fjolårets bokförda utfall → kommentarer ──
  const generera = async () => {
    if (!D || kommJobb) return;
    setKommJobb(true); setKommFel(''); setKommMsg('');
    try {
      const fjolRa = String(Number(ra.slice(0, 2)) - 1).padStart(2, '0') + ra.slice(0, 2);
      const fjolMan = new Set(raManader(fjolRa));
      const bolagPayload = [];
      for (const b of D.bolagMed) {
        // Fjolårets utfall ur SIE-uppladdningen (intel_bokforing)
        let utfallIfjol = null;
        const snap = await getDocs(query(collection(db, 'intel_bokforing'), where('bolag', '==', b.id)));
        const sum = { intakt: 0, vk: 0, ovrigt: 0, personal: 0, avskr: 0, finans: 0 };
        let traff = false;
        snap.forEach((dd) => {
          const v = dd.data();
          if (v.typ === 'meta' || !v.summor || !fjolMan.has(v.ym)) return;
          traff = true;
          for (const g of Object.keys(sum)) sum[g] += v.summor[g] || 0;
        });
        if (traff) utfallIfjol = { ...sum, resultat: sum.intakt - sum.vk - sum.ovrigt - sum.personal - sum.avskr - sum.finans };
        bolagPayload.push({
          id: b.id, namn: b.namn,
          rr: D.perBolag[b.id].rr,
          utfallIfjol,
          forutsattningar: D.perBolag[b.id].forutsattningar || '',
        });
      }
      // Assistenten får riktiga banksaldon i stället för periodiserade
      // resultatrader — utan ingående saldo gick det inte att uttala sig om
      // huruvida pengarna faktiskt räcker.
      const likviditet = D.likvid.map((r, i) => ({
        manad: MANAD_LABELS[i], ingaende: Math.round(r.ingaende), inbet: Math.round(r.inbet),
        utbet: Math.round(r.utbet), likvid: Math.round(r.likvid), utgaende: Math.round(r.utgaende),
      }));
      const res = await styrelseUtkast({ raLabel: raLabel2(ra), version, bolag: bolagPayload, likviditet, anteckning });
      const perBolag = {};
      for (const bk of (res.bolagskommentarer || [])) perBolag[bk.bolag] = bk.text;
      setKomm({ sammanfattning: res.sammanfattning || '', likviditetskommentar: res.likviditetskommentar || '', perBolag });
      setKommDirty(true);
      setKommMsg('Utkast klart — läs igenom, justera och spara.');
    } catch (e) {
      setKommFel(e.message || 'Kunde inte generera utkast');
    } finally { setKommJobb(false); }
  };

  const sparaKomm = async () => {
    if (kommSparar) return;
    setKommSparar(true); setKommFel('');
    try {
      await setDoc(doc(db, 'intel_budget', `rapport_${ra}_${version}`), {
        typ: 'rapportkommentar', ra, version,
        sammanfattning: komm.sammanfattning,
        likviditetskommentar: komm.likviditetskommentar,
        perBolag: komm.perBolag,
        updatedAt: serverTimestamp(),
        updatedBy: auth.currentUser?.email || '',
      });
      setKommDirty(false); setKommMsg('✓ Kommentarerna sparade');
    } catch (e) {
      setKommFel(`Kunde inte spara: ${e.message}`);
    } finally { setKommSparar(false); }
  };

  // Resultatvy och likvidvy delar de fyra driftsraderna. Skillnaden nedanför
  // driftsresultatet: avskrivningar är en kostnad utan kassaflöde, amorteringar
  // ett kassaflöde utan kostnad. Anståndet får egen rad — det är en tillfällig
  // skuld som ska bort, inte en löpande post.
  const DRIFT = [
    ['Rörelsens intäkter', 'intakter', 1],
    ['Material och varor', 'material', -1],
    ['Övriga externa kostnader', 'ovriga', -1],
    ['Personal', 'personal', -1],
    ['Driftsresultat', 'driftsresultat', 1, true],
  ];
  const RR_RADER = vy === 'likvid'
    ? [...DRIFT,
      ['Finansiella poster', 'finans', -1],
      ['Amorteringar', 'amortering', -1],
      ['Amortering anstånd skatt', 'amortSkatt', -1],
      ['Investeringar', 'investering', -1],
      ['Erhållna amorteringar', 'amorteringIn', 1],
      ['Extraordinära poster', 'extraNetto', 1],
      // Raderna ovan är periodiserade. Kassan påverkas av NÄR fakturorna
      // betalas: förra årets skulder betalas i juli, årets junikostnader
      // först i juli nästa år. Utan den här raden stämmer inte bottenraden
      // mot månadstabellen.
      ['Likvidresultat före förskjutning', 'likvidFore', 1, true],
      ['Betalningsförskjutning', 'kassajustering', 1],
      ['Likvidresultat', 'likvidResultat', 1, true]]
    : [...DRIFT,
      ['Avskrivningar', 'avskr', -1],
      ['Finansiella poster', 'finans', -1],
      ['Resultat', 'resultat', 1, true]];

  // Kolumner: bolagen, plus O/H när det är utbrutet. D är null tills
  // budgetdokumenten laddats — vyn renderas inte då, men uttrycket beräknas
  // ändå på väg dit.
  const kolumner = !D ? [] : [
    ...D.bolagMed.map((b) => ({
      key: b.id,
      namn: b.namn.replace(' AB', '') + (version === PROGNOS || D.perBolag[b.id].version !== version ? ` (${D.perBolag[b.id].version})` : ''),
      rr: ohUt ? D.utanOh[b.id] : D.perBolag[b.id].rr,
    })),
    ...(ohUt ? [{ key: '_oh', namn: 'O/H koncern', rr: D.ohKol, oh: true }] : []),
  ];
  const chip = (aktiv) => ({
    background: aktiv ? '#44365C' : 'transparent', color: aktiv ? '#fff' : 'var(--text-secondary)',
    border: '1px solid var(--border)', borderRadius: 20, padding: '4px 12px',
    fontSize: 11.5, cursor: 'pointer', fontFamily: 'system-ui,sans-serif',
  });

  return (
    <>
      <div className="card" style={{ paddingTop: 12, paddingBottom: 12 }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', fontFamily: 'system-ui,sans-serif' }}>
          {raVal().map((r) => <button key={r} onClick={() => setRa(r)} style={btn(ra === r)}>{raLabel2(r)}</button>)}
          <div style={{ width: 1, alignSelf: 'stretch', background: 'var(--border)', margin: '0 4px' }} />
          {versioner.map((v) => <button key={v} onClick={() => setVersion(v)} style={btn(version === v)}>{v}</button>)}
          <span style={{ fontSize: 11, color: 'var(--text-secondary)', marginLeft: 6 }}>Senaste prognos = varje bolags högsta P-version. Bolag utan vald version faller tillbaka på B0.</span>
        </div>
      </div>

      {!D && <div className="card"><div style={{ fontSize: 13, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif' }}>Laddar…</div></div>}
      {D && D.bolagMed.length === 0 && (
        <div className="card"><div style={{ fontSize: 13, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif' }}>Ingen budget sparad för {raLabel2(ra)} ännu — lägg den i Budgetmatrisen först.</div></div>
      )}
      {D && D.bolagMed.length > 0 && (
        <>
          {/* Kort resultatrapport — koncern */}
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
              <div className="card-title" style={{ marginBottom: 0 }}>
                Koncernbudget {raLabel2(ra)} ({version === PROGNOS ? 'senaste prognos' : version}) — kort {vy === 'likvid' ? 'likviditetsrapport' : 'resultatrapport'}, helår
              </div>
              <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }} data-export-skip="1">
                <button onClick={() => setVy('resultat')} style={chip(vy === 'resultat')}>Resultat</button>
                <button onClick={() => setVy('likvid')} style={chip(vy === 'likvid')}>Likviditet</button>
                <div style={{ width: 1, alignSelf: 'stretch', background: 'var(--border)', margin: '0 3px' }} />
                <button onClick={() => setOhUt((o) => !o)} style={chip(ohUt)}>O/H utbrutet</button>
              </div>
            </div>
            <div className="period-table">
              <div className="period-header" style={{ display: 'grid', gridTemplateColumns: `1.4fr repeat(${kolumner.length + 1}, 1fr)`, gap: 4 }}>
                <span></span>
                {kolumner.map((k) => <span key={k.key} style={{ textAlign: 'right', fontStyle: k.oh ? 'italic' : 'normal' }}>{k.namn}</span>)}
                <span style={{ textAlign: 'right' }}>Totalt</span>
              </div>
              {RR_RADER.map(([label, falt, tecken, fet]) => (
                <div key={falt} className="period-row" style={{ display: 'grid', gridTemplateColumns: `1.4fr repeat(${kolumner.length + 1}, 1fr)`, gap: 4, fontWeight: fet ? 700 : 400, background: fet ? 'var(--panel)' : 'transparent' }}>
                  <span>{label}</span>
                  {kolumner.map((k) => {
                    const v = (k.rr[falt] || 0) * tecken;
                    return <span key={k.key} style={{ textAlign: 'right', color: v < 0 ? '#A32D2D' : 'inherit', fontStyle: k.oh ? 'italic' : 'normal' }}>{krFmt(v)}</span>;
                  })}
                  {(() => { const v = (D.tot[falt] || 0) * tecken; return <span style={{ textAlign: 'right', color: v < 0 ? '#A32D2D' : 'inherit', fontWeight: 700 }}>{krFmt(v)}</span>; })()}
                </div>
              ))}
              {/* Vem som BETALAR O/H internt. Bäraren — bolaget som fakturerar
                  ut avgiften — har sin egen overheadkostnad på samma rad i
                  modellen, och den är ingen internpost. Skiljs de inte åt ser
                  det ut som att bäraren också betalar en avgift till sig själv. */}
              {/* Utbrytningen är en LEDNINGSVY. Juridiskt bär Hotell hela
                  overheaden och får bara vidarefaktureringen tillbaka, så
                  bolagets verkliga resultat är ett annat. Utan den här raden
                  ser Hotell ut att tjäna 2,7 mkr när årsredovisningen kommer
                  att visa 24 tkr. */}
              {ohUt && (
                <div className="period-row" style={{ display: 'grid', gridTemplateColumns: `1.4fr repeat(${kolumner.length + 1}, 1fr)`, gap: 4, borderTop: '2px solid var(--border)', fontStyle: 'italic', color: 'var(--text-secondary)' }}>
                  <span>Resultat i bolaget (O/H kvar)</span>
                  {kolumner.map((k) => (
                    <span key={k.key} style={{ textAlign: 'right' }}>
                      {k.oh ? '–' : krFmt((D.perBolag[k.key].rr[vy === 'likvid' ? 'likvidResultat' : 'resultat']) || 0)}
                    </span>
                  ))}
                  <span style={{ textAlign: 'right' }}>{krFmt(D.tot[vy === 'likvid' ? 'likvidResultat' : 'resultat'])}</span>
                </div>
              )}
              {ohUt && (
                <div className="period-row" style={{ display: 'grid', gridTemplateColumns: `1.4fr repeat(${kolumner.length + 1}, 1fr)`, gap: 4, fontStyle: 'italic', color: 'var(--text-secondary)' }}>
                  <span>varav internt O/H-bidrag</span>
                  {kolumner.map((k) => {
                    const v = k.oh ? D.tot.ohIntakt
                      : (k.rr.ohIntakt > 0 ? 0 : -(k.rr.ohOvrigt || 0));
                    return <span key={k.key} style={{ textAlign: 'right', color: v < 0 ? '#A32D2D' : v > 0 ? '#1D6A5F' : 'var(--text-secondary)' }}>{v ? krFmt(v) : '–'}</span>;
                  })}
                  {(() => {
                    const betalt = D.bolagMed.reduce((s, b) => s + (D.perBolag[b.id].rr.ohIntakt > 0 ? 0 : D.perBolag[b.id].rr.ohOvrigt), 0);
                    return <span style={{ textAlign: 'right' }}>{krFmt(D.tot.ohIntakt - betalt)}</span>;
                  })()}
                </div>
              )}
            </div>
            <div className="note">
              Helår {raLabel2(ra)}. Kostnader visas med minustecken. Version inom parentes = den version bolaget faktiskt visar.
              {vy === 'likvid' && ' Likvidvyn byter avskrivningar mot amorteringar och investeringar — kostnad utan kassaflöde mot kassaflöde utan kostnad. Anståndsamorteringen har egen rad eftersom den är en tillfällig skuld som ska bort, inte en löpande post. Likvidresultat FÖRE förskjutning visar årets underliggande förmåga att bygga kapital. Betalningsförskjutningen fångar sedan att förra årets skulder betalas i juli medan årets junikostnader betalas först i juli nästa år, och bottenraden stämmer därmed mot månadstabellen.'}
              {ohUt && ' O/H-kolumnen samlar de O/H-märkta raderna — ledning och adm. under Personalkostnader, koncerngemensamt under Övriga kostnader — plus den vidarefakturerade O/H-intäkten. Varje post lyfts ur sin egen kostnadsgrupp, så Personal% förblir korrekt. Bolagen visas på ren drift och totalen är oförändrad. Utbrytningen är en LEDNINGSVY: juridiskt bär Hotell hela overheaden och får bara vidarefaktureringen tillbaka, vilket raden Resultat i bolaget visar.'}
              {ohUt && D.tot.ohKostnad === 0 && ' OBS: båda O/H-raderna är tomma, så kolumnen visar bara vidarefaktureringen. Flytta ledningens löner till O/H — ledning & adm. och de gemensamma kostnaderna till O/H — koncerngemensamt i budgetmatrisen.'}
            </div>
          </div>

          {/* Prognos mot fastställd budget — koncern */}
          {JMF && (() => {
            const RADERJ = [
              ['Rörelsens intäkter', 'intakter', 1],
              ['Material och varor', 'direkta', -1],
              ['Övriga externa kostnader', 'ovriga', -1],
              ['Personal', 'personal', -1],
              ['Driftsresultat', 'driftsresultat', 1, true],
              ['Avskrivningar', 'avskr', -1],
              ['Finansiella poster', 'finans', -1],
              ['Resultat', 'resultat', 1, true],
            ];
            const manEtikett = (ym) => (ym ? `${MANAD_LABELS[manader.indexOf(ym)]} ${ym.slice(2, 4)}` : '');
            const hittillsTxt = JMF.gemensamTom
              ? `jul–${manEtikett(JMF.gemensamTom)}`
              : 'utfallsmånaderna';
            const harUtfall = JMF.tomPerBolag.some((t) => t.tom);
            const grid = { display: 'grid', gridTemplateColumns: '1.6fr repeat(7, 1fr)', gap: 4 };
            const fgTxt = JMF.gemensamTom
              ? `Fg år jul–${manEtikett(JMF.gemensamTom).replace(/\d+$/, (y) => String(Number(y) - 1).padStart(2, '0'))}`
              : 'Fg år';
            const avv = (diff, fet) => (
              <span style={{ textAlign: 'right', fontWeight: fet ? 700 : 600, color: diff > 0 ? GRON : diff < 0 ? ROD : 'var(--text-secondary)' }}>
                {diff ? `${diff > 0 ? '+' : ''}${krFmt(diff)}` : '–'}
              </span>
            );
            const tal = (v, fet) => <span style={{ textAlign: 'right', fontWeight: fet ? 700 : 400, color: v < 0 ? ROD : 'inherit' }}>{krFmt(v)}</span>;
            const rad = (label, v, tecken, fet, key, stil = {}) => (
              <div key={key} className="period-row" style={{ ...grid, fontWeight: fet ? 700 : 400, background: fet ? 'var(--panel)' : 'transparent', ...stil }}>
                <span>{label}</span>
                {harUtfall ? (
                  <>
                    {tal(v.hBud * tecken, fet)}{tal(v.hUtf * tecken, fet)}
                    {JMF.fgLaddad
                      ? <span style={{ textAlign: 'right', fontWeight: fet ? 700 : 400, color: 'var(--text-secondary)' }}>{krFmt(v.fg * tecken)}</span>
                      : <span style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>…</span>}
                    {avv((v.hUtf - v.hBud) * tecken, fet)}
                  </>
                ) : <><span /><span /><span /><span /></>}
                {tal(v.b0 * tecken, fet)}{tal(v.p * tecken, fet)}{avv((v.p - v.b0) * tecken, fet)}
              </div>
            );
            return (
              <div className="card">
                <div className="card-title">
                  {version === PROGNOS ? 'Senaste prognos' : `Prognos ${version}`} mot fastställd budget B0 — koncern {raLabel2(ra)}
                </div>
                <div className="period-table">
                  <div className="period-header" style={grid}>
                    <span></span>
                    <span style={{ textAlign: 'right' }}>{harUtfall ? `Budget ${hittillsTxt}` : ''}</span>
                    <span style={{ textAlign: 'right' }}>{harUtfall ? 'Utfall' : ''}</span>
                    <span style={{ textAlign: 'right' }}>{harUtfall ? fgTxt : ''}</span>
                    <span style={{ textAlign: 'right' }}>{harUtfall ? 'Avvikelse' : ''}</span>
                    <span style={{ textAlign: 'right', borderLeft: '1px solid var(--border)' }}>Budget helår</span>
                    <span style={{ textAlign: 'right' }}>Prognos helår</span>
                    <span style={{ textAlign: 'right' }}>Avvikelse</span>
                  </div>
                  {RADERJ.map(([label, f, tecken, fet]) => rad(label, JMF.tot[f], tecken, fet, f))}
                  {D.bolagMed.filter((b) => JMF.per[b.id]).map((b) => rad(
                    `Resultat — ${b.namn.replace(' AB', '')}`, JMF.per[b.id].resultat, 1, false, `res-${b.id}`,
                    { fontStyle: 'italic', color: 'var(--text-secondary)' },
                  ))}
                </div>
                <div className="note">
                  {harUtfall
                    ? <>Hittills = de månader där prognosen har bokfört utfall inlagt ({JMF.tomPerBolag.map((t) => `${t.namn} ${t.version} t.o.m. ${manEtikett(t.tom) || '–'}`).join(' · ')}). </>
                    : <>Ingen version har utfall inlagt än — lägg in det med <strong>📒 Lägg in utfall</strong> i budgetmatrisen. </>}
                  {harUtfall && <>Fg år = samma månader ett år tidigare ur den inlästa bokföringen{JMF.fgSaknas.length ? ` — saknas helt eller delvis för ${JMF.fgSaknas.join(', ')}` : ''}. Avvikelsen avser utfall mot budget. </>}
                  Prognos helår = utfall för de stängda månaderna plus prognosversionens budget för resten av året.
                  Grönt är bättre än budget — för kostnader betyder det lägre.
                  {JMF.tomPerBolag.some((t) => t.version === 'B0') && ` Bolag utan prognosversion visar B0 på båda sidor och ger därför ingen avvikelse.`}
                </div>
              </div>
            );
          })()}

          {/* Likviditetsbudget per månad — koncern */}
          <div className="card">
            <div className="card-title">Likviditetsbudget {raLabel2(ra)} ({version === PROGNOS ? 'senaste prognos' : version}) — koncern, per månad (tkr)</div>
            <div style={{ display: 'flex', gap: 26, flexWrap: 'wrap', marginBottom: 10, fontFamily: 'system-ui,sans-serif' }}>
              <div>
                <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Ingående bank</div>
                <div style={{ fontSize: 17, fontWeight: 700 }}>{Math.round(D.ingaendeBank / 1000).toLocaleString('sv-SE')} tkr</div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Lägsta saldo ({D.lagsta.label})</div>
                <div style={{ fontSize: 17, fontWeight: 700, color: D.lagsta.utgaende < 0 ? '#A32D2D' : '#3B6D11' }}>
                  {Math.round(D.lagsta.utgaende / 1000).toLocaleString('sv-SE')} tkr
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Utgående bank</div>
                <div style={{ fontSize: 17, fontWeight: 700 }}>
                  {Math.round(D.likvid[D.likvid.length - 1].utgaende / 1000).toLocaleString('sv-SE')} tkr
                </div>
              </div>
            </div>
            <div className="period-table">
              <div className="period-header period-row-6">
                <span>Månad</span><span>Ingående</span><span>Inbetalningar</span><span>Utbetalningar</span><span>Kassaflöde</span><span>Utgående</span>
              </div>
              {D.likvid.map((r, i) => {
                const t = (v) => Math.round(v / 1000).toLocaleString('sv-SE');
                return (
                  <div key={i} className="period-row period-row-6">
                    <span>{r.label}</span>
                    <span style={{ color: r.ingaende < 0 ? '#A32D2D' : 'inherit' }}>{t(r.ingaende)}</span>
                    <span>{t(r.inbet)}</span>
                    <span>{t(-r.utbet)}</span>
                    <span style={{ fontWeight: 600, color: r.likvid < 0 ? '#A32D2D' : '#3B6D11' }}>{t(r.likvid)}</span>
                    <span style={{ fontWeight: 700, color: r.utgaende < 0 ? '#A32D2D' : 'inherit' }}>{t(r.utgaende)}</span>
                  </div>
                );
              })}
            </div>
            <div className="note">
              Samma modell som Likviditetsmatrisen: budgetens rader förskjutna enligt betalningsreglerna, justerade
              för förskott, med avskrivningar borttagna och investeringar och amorteringar tillagda. Kurvan utgår
              från ingående bank, så <strong>utgående saldo är ett verkligt banksaldo</strong> och inte bara en
              förändring. Lägsta punkten är likviditetsbehovet. <strong>Momsen ingår inte än.</strong>
            </div>
          </div>

          {/* Kurvan */}
          <div className="card">
            <div className="card-title">Ackumulerat över året — likviditet vs resultat (koncern)</div>
            <TrendCompare data={D.kurva} formatVal={(v) => `${Math.round(v / 1000).toLocaleString('sv-SE')} tkr`}
              curColor={ACCENT} prevColor="#1D9E75" curLabel="Likvid ack." prevLabel="Resultat ack." utanDiff />
            <div className="note">Likvidkurvan är banksaldot månad för månad, resultatkurvan det ackumulerade resultatet. Lägsta punkten på likvidkurvan = årets tightaste läge.</div>
          </div>

          {/* 24 månader i en följd — styrelsens längre horisont */}
          {LANG && (
            <div className="card">
              <div className="card-title">
                Likviditet 24 månader — koncernen · {raLabel2(ra)} → {raLabel2(ra2)}
              </div>
              {LANG.saknas ? (
                <div className="note" style={{ marginTop: 0 }}>
                  Ingen budget finns för {raLabel2(ra2)} ännu. Lägg den — gärna med
                  <strong> Generera förslag → Budget</strong> i budgetmatrisen — så ritas kurvan här.
                </div>
              ) : (
                <>
                  <div style={{ display: 'flex', gap: 22, flexWrap: 'wrap', marginBottom: 6, fontFamily: 'system-ui,sans-serif' }}>
                    {[
                      ['Ingående bank', LANG.start, null],
                      ['Lägsta punkt', LANG.lagsta.utgaende, `${LANG.lagsta.label} ${LANG.lagsta.ym.slice(2, 4)}`],
                      ['Efter 24 månader', LANG.slut, null],
                    ].map(([etikett, v, sub]) => (
                      <div key={etikett}>
                        <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{etikett}{sub ? ` (${sub})` : ''}</div>
                        <div style={{ fontSize: 17, fontWeight: 700, color: v < 0 ? ROD : GRON }}>{tkr(v)}</div>
                      </div>
                    ))}
                  </div>
                  <LangKurva punkter={LANG.punkter} brytIdx={LANG.brytIdx} anstandIdx={LANG.anstandIdx} />
                  {LANG.glapp !== 0 && (
                    <div style={{ marginTop: 6, fontSize: 12, color: ROD, fontFamily: 'system-ui,sans-serif' }}>
                      ⚠ Kurvan är bruten vid årsskiftet: {raLabel2(ra2)} börjar {tkr(LANG.glapp)} från där
                      {' '}{raLabel2(ra)} slutar. Kör <strong>↩ Hämta ur {raLabel2(ra)}</strong> i likviditetsmatrisen
                      för vart och ett av bolagen, annars visar kurvan ett hopp som inte finns.
                    </div>
                  )}
                  <div className="note">
                    Samma modell som ettårskurvan, två räkenskapsår efter varandra. Markören visar den sista
                    månaden med amortering på skatteanståndet — den läses ur budgetraden, så flyttar
                    markören sig om planen ändras. <strong>Refinansieringen av banklånen ligger inte i kurvan</strong>; den
                    syns först när räntor och amorteringar ändras i budgeten. Kurvan visar alltså läget
                    <strong> utan</strong> den lättnaden. Momsen ingår inte än.
                    {LANG.versioner ? ` År två: ${LANG.versioner}.` : ''}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Kommentarer till styrelsen (AI-utkast, redigerbart) */}
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
              <div className="card-title" style={{ marginBottom: 0 }}>Kommentarer till styrelsen</div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                <input value={anteckning} onChange={(e) => setAnteckning(e.target.value)}
                  placeholder='Anteckning till utkastet (valfritt)…'
                  style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '6px 10px', fontSize: 12, fontFamily: 'system-ui,sans-serif', background: 'var(--card)', color: 'var(--text)', width: 240 }} />
                <button onClick={generera} disabled={kommJobb}
                  style={{ background: ACCENT, color: '#fff', border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 12.5, cursor: 'pointer', fontFamily: 'system-ui,sans-serif', opacity: kommJobb ? 0.6 : 1 }}>
                  {kommJobb ? '✍️ Skriver…' : '✨ Generera utkast'}
                </button>
                <button onClick={sparaKomm} disabled={!kommDirty || kommSparar}
                  style={{ background: kommDirty ? '#2E7D32' : 'transparent', color: kommDirty ? '#fff' : 'var(--text-secondary)', border: '1px solid var(--border)', borderRadius: 8, padding: '6px 14px', fontSize: 12.5, cursor: 'pointer', fontFamily: 'system-ui,sans-serif' }}>
                  {kommSparar ? 'Sparar…' : '💾 Spara'}
                </button>
              </div>
            </div>
            {kommFel && <div style={{ fontSize: 12, color: '#A32D2D', fontFamily: 'system-ui,sans-serif', marginBottom: 8 }}>{kommFel}</div>}
            {kommMsg && !kommFel && <div style={{ fontSize: 12, color: '#2E7D32', fontFamily: 'system-ui,sans-serif', marginBottom: 8 }}>{kommMsg}</div>}

            {[
              ['sammanfattning', 'Sammanfattning — koncernen'],
              ['likviditetskommentar', 'Likviditetsbedömning'],
            ].map(([falt, rubrik]) => (
              <div key={falt} style={{ marginBottom: 10, fontFamily: 'system-ui,sans-serif' }}>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-secondary)', marginBottom: 3 }}>{rubrik}</div>
                <textarea value={komm[falt]}
                  onChange={(e) => { setKomm((k) => ({ ...k, [falt]: e.target.value })); setKommDirty(true); }}
                  placeholder="Genereras med ✨-knappen eller skrivs för hand…"
                  style={{ width: '100%', minHeight: 72, border: '1px solid var(--border)', borderRadius: 8, padding: 8, fontSize: 12.5, lineHeight: 1.5, fontFamily: 'system-ui,sans-serif', background: 'var(--card)', color: 'var(--text)', resize: 'vertical' }} />
              </div>
            ))}
            {D.bolagMed.map((b) => (
              <div key={b.id} style={{ marginBottom: 10, fontFamily: 'system-ui,sans-serif' }}>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-secondary)', marginBottom: 3 }}>{b.namn}</div>
                <textarea value={komm.perBolag[b.id] || ''}
                  onChange={(e) => { setKomm((k) => ({ ...k, perBolag: { ...k.perBolag, [b.id]: e.target.value } })); setKommDirty(true); }}
                  placeholder="Genereras med ✨-knappen eller skrivs för hand…"
                  style={{ width: '100%', minHeight: 56, border: '1px solid var(--border)', borderRadius: 8, padding: 8, fontSize: 12.5, lineHeight: 1.5, fontFamily: 'system-ui,sans-serif', background: 'var(--card)', color: 'var(--text)', resize: 'vertical' }} />
              </div>
            ))}
            <div className="note">
              Utkastet bygger på budgetens resultatrapport, likviditetsbudgeten, fjolårets bokförda utfall (SIE-uppladdningen) och förutsättningarna.
              AI:n publicerar aldrig något — texten sparas först när du klickar Spara, per räkenskapsår och version.
            </div>
          </div>

          {/* Förutsättningar */}
          {D.bolagMed.some((b) => D.perBolag[b.id].forutsattningar) && (
            <div className="card">
              <div className="card-title">Förutsättningar</div>
              {D.bolagMed.filter((b) => D.perBolag[b.id].forutsattningar).map((b) => (
                <div key={b.id} style={{ marginBottom: 10, fontFamily: 'system-ui,sans-serif' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-secondary)', marginBottom: 3 }}>{b.namn}</div>
                  <div style={{ fontSize: 12.5, whiteSpace: 'pre-wrap' }}>{D.perBolag[b.id].forutsattningar}</div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </>
  );
}
